import { ConnectCall, UserStatus } from "@ameelio/connect-call-client";
import { Button, IconButton } from "@ameelio/ui";
import BackHandOutlinedIcon from "@mui/icons-material/BackHandOutlined";
import CloseIcon from "@mui/icons-material/Close";
import MessageOutlinedIcon from "@mui/icons-material/MessageOutlined";
import MicOffOutlinedIcon from "@mui/icons-material/MicOffOutlined";
import MicOutlinedIcon from "@mui/icons-material/MicOutlined";
import { Box, Grow, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Correspondent } from "../../api/graphql";
import ChatInput from "./ChatInput";
import ChatMessage from "./ChatMessage";
import { RemoteParticipant } from "./types/ConnectionInfo";

type WebinarQuestionProps = {
  correspondent: Pick<Correspondent, "id" | "fullName">;
  currentUserId: string;
  contents: string;
  timestamp: Date;
  id: string;
  onDismiss: (id: string) => Promise<void>;
};

type Props = {
  currentUserId: string;
  participants: RemoteParticipant[];
  correspondents: WebinarQuestionProps["correspondent"][];
  dismissWebinarQuestion: (id: string) => Promise<void>;
  sendWebinarAnnouncement: (id: string) => Promise<void>;
  dismissWebinarAnnouncement: (id: string) => Promise<void>;
  remoteLowerHand: (id: string) => void;
  muteSounds?: boolean;
  remoteAudioUnmute: (id: string) => void;
  remoteAudioMute: (id: string) => void;
  announcements: ConnectCall["webinarAnnouncements"];
  questions: ConnectCall["webinarQuestions"];
  onClose: () => void;
};

export default function WebinarHostQAndA({
  currentUserId,
  correspondents,
  participants,
  remoteLowerHand,
  muteSounds,
  remoteAudioUnmute,
  remoteAudioMute,
  dismissWebinarQuestion,
  dismissWebinarAnnouncement,
  questions,
  announcements,
  sendWebinarAnnouncement,
  onClose,
}: Props) {
  const { t } = useTranslation();

  const textQuestionItems: (
    | {
        type: "question";
        timestamp: number;
        question: ConnectCall["webinarQuestions"][0];
      }
    | {
        type: "participant";
        timestamp: number;
        participant: RemoteParticipant;
      }
  )[] = questions.map((question) => ({
    type: "question",
    timestamp: question.timestamp,
    question,
  }));
  const questionItems = textQuestionItems
    .concat(
      participants
        .filter((p) => p.status.includes(UserStatus.HandRaised))
        .map((participant) => ({
          type: "participant",
          timestamp: participant.lastRaisedHand,
          participant,
        }))
    )
    .sort((a, b) => a.timestamp - b.timestamp);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: 1 }}>
      <Box sx={{ display: "flex", padding: 2 }}>
        <Typography variant="h1" component="div" sx={{ flexGrow: 1 }}>
          {t("Class")}
        </Typography>
        <IconButton size="small" ariaLabel={t("Close")} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "scroll",
          px: 2,
        }}
      >
        <Stack spacing={3}>
          <Stack spacing={2}>
            <Typography variant="h3">
              {t("Announcements")} ({announcements.length})
            </Typography>
            {announcements.length ? (
              <Stack spacing={1}>
                {announcements.map((m) => {
                  // lookups are risky. i don't know how to avoid this one yet.
                  const correspondent = correspondents.find(
                    (p) => p.id === m.author.id
                  ) || {
                    id: "",
                    firstName: "",
                    lastName: "",
                    fullName: "",
                  };
                  return (
                    <Grow
                      in
                      timeout={500}
                      style={{ transformOrigin: "bottom center" }}
                      key={m.timestamp}
                    >
                      <Box>
                        <ChatMessage
                          currentUserId={currentUserId}
                          correspondent={correspondent}
                          contents={m.contents}
                          timestamp={new Date(m.timestamp)}
                          iconSlot={
                            <MessageOutlinedIcon
                              sx={{ height: 20, width: 20 }}
                            />
                          }
                          onClose={() => dismissWebinarAnnouncement(m.id)}
                        />
                      </Box>
                    </Grow>
                  );
                })}
              </Stack>
            ) : null}
            <ChatInput
              label={t("Message")}
              onSend={sendWebinarAnnouncement}
              muteSounds={muteSounds}
            />
          </Stack>
          <Stack spacing={2}>
            <Typography variant="h3">
              {t("Questions ({{count}})", { count: questionItems.length })}
            </Typography>
            <Stack spacing={1}>
              {questionItems.map((item) => {
                if (item.type === "question") {
                  const m = item.question;

                  // lookups are risky. i don't know how to avoid this one yet.
                  const correspondent = correspondents.find(
                    (p) => p.id === m.author.id
                  ) || {
                    id: "",
                    firstName: "",
                    lastName: "",
                    fullName: "",
                  };
                  return (
                    <Grow
                      in
                      timeout={500}
                      style={{ transformOrigin: "bottom center" }}
                      key={m.timestamp}
                    >
                      <Box>
                        <ChatMessage
                          currentUserId={currentUserId}
                          correspondent={correspondent}
                          contents={m.contents}
                          timestamp={new Date(m.timestamp)}
                          iconSlot={
                            <MessageOutlinedIcon
                              sx={{ height: 20, width: 20 }}
                            />
                          }
                          onClose={() => dismissWebinarQuestion(m.id)}
                        />
                      </Box>
                    </Grow>
                  );
                } else {
                  const participant = item.participant;

                  const correspondent = correspondents.find(
                    (p) => p.id === participant.user.id
                  ) || {
                    id: "",
                    firstName: "",
                    lastName: "",
                    fullName: "",
                  };
                  const muted = participant.status.includes(
                    UserStatus.AudioMutedByServer
                  );
                  return (
                    <Grow
                      in
                      timeout={500}
                      style={{ transformOrigin: "bottom center" }}
                      key={participant.user.id}
                    >
                      <Box>
                        <ChatMessage
                          currentUserId={currentUserId}
                          correspondent={correspondent}
                          timestamp={new Date(participant.lastRaisedHand)}
                          iconSlot={
                            <BackHandOutlinedIcon
                              sx={{ height: 20, width: 20 }}
                            />
                          }
                          onClose={() => remoteLowerHand(correspondent.id)}
                        >
                          <Button
                            onClick={() =>
                              muted
                                ? remoteAudioUnmute(correspondent.id)
                                : remoteAudioMute(correspondent.id)
                            }
                            size="small"
                            variant="outlined"
                            sx={{ width: "100%" }}
                          >
                            {muted ? (
                              <>
                                <MicOffOutlinedIcon /> {t("Unmute")}
                              </>
                            ) : (
                              <>
                                <MicOutlinedIcon /> {t("Mute")}
                              </>
                            )}
                          </Button>
                        </ChatMessage>
                      </Box>
                    </Grow>
                  );
                }
              })}
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
